import { createDateFormatter, displayDate, useDisplayTimezone, type TimezoneMode } from "@nexus/ui";
import { EmptyDataState, formatDetectionDomain, useChartLifecycle } from "@nexus/widget-kit";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ToggleChip } from "@nexus/ui";
import * as echarts from "echarts/core";
import { LineChart, type LineSeriesOption } from "echarts/charts";
import { LegacyGridContainLabel } from "echarts/features";
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TooltipComponent,
  type DataZoomComponentOption,
  type GridComponentOption,
  type LegendComponentOption,
  type MarkAreaComponentOption,
  type MarkLineComponentOption,
  type MarkPointComponentOption,
  type TooltipComponentOption,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  firstError,
  resolveChartColors,
  resolveLabels,
  unwrap,
  Widget,
  WidgetContentReadyContext,
  WidgetFullscreenContext,
  withAlpha,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type { JsonValue } from "@nexus/widget-sdk";
import type {
  EventEvolutionBreakdownEntry,
  EventEvolutionBucketCount,
  EventEvolutionConfig,
  EventEvolutionData,
  EventEvolutionPoint,
} from "./types";
import { DEFAULT_LABELS, LABELS, type EventEvolutionLabels } from "./labels";
import { InfoTip } from "@nexus/ui";

echarts.use([
  LineChart,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TooltipComponent,
  CanvasRenderer,
  LegacyGridContainLabel,
]);

type EventEvolutionOption = echarts.ComposeOption<
  | LineSeriesOption
  | DataZoomComponentOption
  | GridComponentOption
  | LegendComponentOption
  | TooltipComponentOption
  | MarkAreaComponentOption
  | MarkLineComponentOption
  | MarkPointComponentOption
>;

export type {
  EventEvolutionConfig,
  EventEvolutionData,
  EventEvolutionPoint,
  EventEvolutionSeriesName,
} from "./types";

export { LABELS } from "./labels";

export const manifest: WidgetManifest = {
  id: "event-evolution",
  name: "Event Evolution",
  description: "Detection, response, and impact evolution around a protected event.",
  version: "1.4.0",
  dataRequests: {
    evolution: {
      source: "event-evolution",
      contract: { name: "event-evolution", version: 1 },
      // Reused report-side (event-detail dashboard) with a dataBindings
      // override to "protection-overview-event-evolution" (see
      // domains.dashboards.builtins). A dataBindings entry only takes effect
      // when the request declares itself configurable — "false" here
      // silently produced "Data request does not allow a source override"
      // and no data ever loaded. The live pages that use the default source
      // (no dataBindings entry) are unaffected by this flag.
      configurable: true,
    },
  },
  labels: LABELS,
};

export const defaultConfig: EventEvolutionConfig = {
  title: "Event Evolution",
  detectionRepresentation: "smooth-line",
};

type SeriesPoint = [number, number];

/**
 * Robust timestamp parser.
 *
 * The backend mixes two shapes on the same payload:
 *   - bucket timestamps are ISO-8601 with an explicit `Z`  (UTC)
 *   - event/blocking/detection markers are *naive*         ("YYYY-MM-DD HH:MM:SS.ffffff")
 *
 * Naive markers are emitted in UTC but drop the designator, so `new Date()` would
 * read them as local time and drift the markers by the local offset. We normalise
 * everything to UTC here so buckets and markers land on the same axis.
 */
function parseTs(value?: string | null): number | null {
  if (value == null) return null;
  let s = String(value).trim();
  if (!s) return null;

  s = s.replace(" ", "T");
  s = s.replace(/(\.\d{3})\d+/, "$1");
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = `${s}Z`;
  }

  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatAxisTime(ms: number, timezone: TimezoneMode): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  if (
    displayDate(date, timezone).getUTCHours() === 0 &&
    displayDate(date, timezone).getUTCMinutes() === 0
  ) {
    return createDateFormatter(timezone, { month: "short", day: "numeric" }).format(date);
  }
  return createDateFormatter(timezone, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

function formatTooltipClock(ms: number, timezone: TimezoneMode): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  return createDateFormatter(timezone, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function titleCaseWords(value: string): string {
  if (!value) return "—";
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Mirror of the system-anomalies `toStatusState` → status token mapping. */
function anomalyStatusTone(status: string): { fg: string; bg: string } {
  switch (status.toLowerCase()) {
    case "resolved":
    case "closed":
    case "cleared":
    case "dismissed":
      return { fg: "var(--status-neutral)", bg: "var(--status-neutral-bg)" };
    case "acknowledged":
    case "investigating":
      return { fg: "var(--status-warn)", bg: "var(--status-warn-bg)" };
    default:
      return { fg: "var(--status-error)", bg: "var(--status-error-bg)" };
  }
}

/**
 * An acknowledged anomaly keeps its colour (the code colour means "anomaly",
 * whatever its state); the state is told by a check mark and a softer line.
 */
const ACKNOWLEDGED_MARK = "\u2713";

function isAcknowledged(status: string | null | undefined): boolean {
  return (status ?? "").toLowerCase() === "acknowledged";
}

interface TooltipAnomaly {
  ms: number;
  anomaly_type: string;
  anomaly_severity: string;
  anomaly_status: string;
  anomaly_description: string | null;
}

/** Anomaly-only tooltip body; stacks multiple anomalies sharing the same minute. */
function renderAnomalyTooltip(
  anomalies: TooltipAnomaly[],
  colors: ChartColors,
  timezone: TimezoneMode,
): string {
  const blocks = anomalies.map((anomaly) => {
    const tone = anomalyStatusTone(anomaly.anomaly_status);
    const badge = `<span style="display:inline-block;padding:1px 7px;border-radius:9999px;font-size:0.75rem;font-weight:600;color:${tone.fg};background:${tone.bg};border:1px solid ${tone.fg}">${isAcknowledged(anomaly.anomaly_status) ? `${ACKNOWLEDGED_MARK} ` : ""}${titleCaseWords(anomaly.anomaly_status)}</span>`;
    const description = anomaly.anomaly_description
      ? `<div style="margin-top:4px;font-size:0.75rem;line-height:1.5;max-width:15rem;white-space:normal">${anomaly.anomaly_description}</div>`
      : "";
    return `<div>
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:3px">
          <span style="width:8px;height:8px;border-radius:2px;background:${colors.anomaly}"></span>
          <span style="font-size:0.75rem;opacity:0.75">${formatTooltipClock(anomaly.ms, timezone)}</span>
        </div>
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
          <span style="font-weight:600">${titleCaseWords(anomaly.anomaly_type)}</span>
          <span style="opacity:0.7">· ${titleCaseWords(anomaly.anomaly_severity)}</span>
          ${badge}
        </div>
        ${description}
      </div>`;
  });
  const divider = `<div style="height:1px;background:${withAlpha(colors.label, 0.15)};margin:7px 0"></div>`;
  return blocks.join(divider);
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function toSeries(points: EventEvolutionPoint[] | undefined): SeriesPoint[] {
  return (points ?? [])
    .map((point): SeriesPoint | null => {
      const ms = parseTs(point.timestamp);
      return ms === null || !Number.isFinite(point.cumulative) ? null : [ms, point.cumulative];
    })
    .filter((p): p is SeriesPoint => p !== null)
    .sort((a, b) => a[0] - b[0]);
}

/**
 * Find the y-value on a series at a given x timestamp by linear interpolation.
 * Used to anchor markPoint diamonds precisely on the curve rather than at y=0.
 */
function interpolateY(series: SeriesPoint[], xMs: number): number {
  if (series.length === 0) return 0;
  if (xMs <= series[0][0]) return series[0][1];
  if (xMs >= series[series.length - 1][0]) return series[series.length - 1][1];

  for (let i = 0; i < series.length - 1; i++) {
    const [x0, y0] = series[i];
    const [x1, y1] = series[i + 1];
    if (xMs >= x0 && xMs <= x1) {
      const t = (xMs - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    }
  }
  return 0;
}

/**
 * Carry a cumulative curve flat to LEAST(now, `until`) (mutates in place).
 *
 * Points sit at the start of their bucket, so the last one is up to a whole
 * bucket (10 minutes) behind the present. A cumulative count never goes down,
 * so holding its last value up to "now" says nothing untrue — and it keeps the
 * curves meeting the NOW line instead of stopping short of it.
 */
export function extendFlatToNow(series: SeriesPoint[], until: number | null, nowMs: number): void {
  if (series.length === 0) return;
  const last = series[series.length - 1];
  const target = Math.min(nowMs, until ?? nowMs);
  if (target > last[0]) series.push([target, last[1]]);
}

export interface EvolutionRefs {
  /** Blue harvesting-start marker (earliest across the selected values). */
  harvestingStart: string | null;
  /** Grey horizontal expected-detections reference (sum across selected values). */
  expectedDetections: number | null;
  /** Vertical anomaly markers — open and acknowledged alike, never dropped. */
  anomalyDetails: EventEvolutionBreakdownEntry["anomaly_details"];
}

export interface EventEvolutionPresentationOptions {
  xAxisLabelFontSize?: number;
  yAxisLabelFontSize?: number;
  xAxisInterval?: number;
  areaFillAlpha?: number;
  detectionLineWidth?: number;
  grid?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  showHarvestingLabel?: boolean;
  showEventWindowLabel?: boolean;
  showExpectedDetectionsLabel?: boolean;
  showAnomalyTypeLabel?: boolean;
  showBlockingLabel?: boolean;
  showNowLabel?: boolean;
}

const EMPTY_REFS: EvolutionRefs = {
  harvestingStart: null,
  expectedDetections: null,
  anomalyDetails: [],
};

function aggregateBucketCounts(
  entries: EventEvolutionBreakdownEntry[],
  pick: (entry: EventEvolutionBreakdownEntry) => EventEvolutionBucketCount[],
): Map<string, number> {
  const acc = new Map<string, number>();
  for (const entry of entries) {
    for (const point of pick(entry) ?? []) {
      acc.set(point.timestamp, (acc.get(point.timestamp) ?? 0) + point.count);
    }
  }
  return acc;
}

/**
 * Recompute the cumulative detection curve and response/impact rates from the
 * per-value `breakdown`, restricted to the `selected` set (empty = all values).
 *
 * The server ships raw per-bucket counts per value; the frontend re-aggregates
 * here on every pill toggle so no refetch is needed. Rates use a shrinking
 * denominator `cumulativeDetections * expectedIsps`.
 */
export function deriveFilteredEvolution(
  data: EventEvolutionData,
  selected: Set<string>,
): { data: EventEvolutionData; refs: EvolutionRefs } {
  const breakdown = data.breakdown ?? [];
  if (breakdown.length === 0) {
    return { data, refs: EMPTY_REFS };
  }

  const entries =
    selected.size === 0 ? breakdown : breakdown.filter((entry) => selected.has(entry.value));

  const detMap = aggregateBucketCounts(entries, (entry) => entry.detections);
  const respMap = aggregateBucketCounts(entries, (entry) => entry.confirmed_responses);
  const pbcMap = aggregateBucketCounts(entries, (entry) => entry.impacts_confirmed);

  const spine = Array.from(
    new Set<string>([...detMap.keys(), ...respMap.keys(), ...pbcMap.keys()]),
  ).sort();

  const isps = data.expected_isps > 0 ? data.expected_isps : 1;

  const detections: EventEvolutionPoint[] = [];
  const responses: EventEvolutionPoint[] = [];
  const impacts: EventEvolutionPoint[] = [];

  let cumDet = 0;
  let cumResp = 0;
  let detectionStart: string | null = null;
  let responseStart: string | null = null;
  let impactStart: string | null = null;
  for (const bucket of spine) {
    const detVal = detMap.get(bucket) ?? 0;
    const respVal = respMap.get(bucket) ?? 0;
    const pbcVal = pbcMap.get(bucket) ?? 0;
    cumDet += detVal;
    cumResp += respVal;
    const denom = cumDet * isps;
    if (detVal > 0 && detectionStart === null) detectionStart = bucket;
    if (respVal > 0 && responseStart === null) responseStart = bucket;
    if (pbcVal > 0 && impactStart === null) impactStart = bucket;

    if (detectionStart !== null) {
      detections.push({ timestamp: bucket, value: detVal, cumulative: cumDet });
    }
    if (responseStart !== null) {
      responses.push({
        timestamp: bucket,
        value: respVal,
        cumulative: denom > 0 ? cumResp / denom : 0,
      });
    }
    if (impactStart !== null) {
      impacts.push({
        timestamp: bucket,
        value: pbcVal,
        cumulative: denom > 0 ? pbcVal / denom : 0,
      });
    }
  }

  const harvestingStarts = entries
    .map((entry) => entry.harvesting_start)
    .filter((value): value is string => Boolean(value));
  // The formatted `YYYY-MM-DD HH:MM:SS` strings sort lexicographically by time.
  const harvestingStart = harvestingStarts.length > 0 ? harvestingStarts.slice().sort()[0] : null;

  const expectedDetections = entries.reduce(
    (sum, entry) => sum + (entry.expected_detection_count || 0),
    0,
  );

  const anomalyDetails = entries.flatMap((entry) => entry.anomaly_details ?? []);

  return {
    data: {
      ...data,
      detections,
      responses,
      impacts,
      detection_start: detectionStart,
      response_start: responseStart,
      impact_start: impactStart,
    },
    refs: {
      harvestingStart,
      expectedDetections: expectedDetections > 0 ? expectedDetections : null,
      anomalyDetails,
    },
  };
}

/** Percent steps that keep 100 % on a tick, from finest to coarsest. */
const EVENT_WINDOW_FILL_ALPHA = 0.16;
const EVENT_WINDOW_EDGE_ALPHA = 0.4;

const RATE_STEPS = [0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10];
const MAX_RATE_TICKS = 6;

export interface AlignedAxes {
  /** Top of the count axis. */
  countMax: number;
  countInterval: number;
  /** Top of the rate axis, as a fraction (1 = 100 %). */
  rateMax: number;
  rateInterval: number;
}

/**
 * Both value axes share one grid, so their ticks can be made to coincide. The
 * reading is "expected detections ↔ 100 %": the rate axis is the count axis
 * divided by the expected count, and 100 % therefore sits exactly on the
 * Expected detections line. Above it the axis simply carries on (110 %, 120 %…).
 *
 * `countPeak` and `ratePeak` are the highest plotted values; nothing is ever
 * clipped to keep the alignment.
 */
export function alignedAxes(expected: number, countPeak: number, ratePeak: number): AlignedAxes {
  const top = Math.max(1, ratePeak, countPeak / expected) * 1.05;
  const step =
    RATE_STEPS.find((candidate) => Math.ceil(top / candidate - 1e-9) <= MAX_RATE_TICKS) ??
    RATE_STEPS[RATE_STEPS.length - 1];
  const ticks = Math.max(1, Math.ceil(top / step - 1e-9));
  const rateMax = Math.round(ticks * step * 1e6) / 1e6;
  return {
    rateInterval: step,
    rateMax,
    countInterval: step * expected,
    countMax: rateMax * expected,
  };
}

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export function buildOption(
  data: EventEvolutionData,
  colors: ChartColors,
  refs: EvolutionRefs,
  applyLegendDefaults: boolean,
  showExpectedDetections: boolean,
  anomaliesVisible: boolean,
  options?: {
    timezone?: TimezoneMode;
    showLegend?: boolean;
    legendSelected?: Record<string, boolean>;
    presentation?: EventEvolutionPresentationOptions;
    detectionRepresentation?: "smooth-line" | "stepped-line" | "stepped-area";
    labels?: EventEvolutionLabels;
  },
): EventEvolutionOption {
  const timezone = options?.timezone ?? "utc";
  const L = options?.labels ?? DEFAULT_LABELS;
  // Config keys (legendSelected / seriesVisibility) always use the canonical series ids.
  const seriesLabel: Record<string, string> = {
    Detections: L.detections,
    "Response %": L.response,
    "Impact %": L.impact,
    "Expected detections": L.expectedDetections,
    Anomalies: L.anomalies,
    Now: L.now,
  };
  const legendSelected = options?.legendSelected
    ? Object.fromEntries(
        Object.entries(options.legendSelected).map(([key, value]) => [
          seriesLabel[key] ?? key,
          value,
        ]),
      )
    : undefined;
  const presentation = options?.presentation;
  const detectionRepresentation = options?.detectionRepresentation ?? "smooth-line";
  const steppedArea = detectionRepresentation === "stepped-area";
  const areaFillAlpha = presentation?.areaFillAlpha ?? 0.16;
  const detectionLineWidth = presentation?.detectionLineWidth ?? 2;
  const xAxisLabelFontSize = presentation?.xAxisLabelFontSize ?? 12;
  const yAxisLabelFontSize = presentation?.yAxisLabelFontSize ?? 12;
  const detectionSeries = toSeries(data.detections);
  const responseSeries = toSeries(data.responses);
  const impactSeries = toSeries(data.impacts);

  const eventStart = parseTs(data.event_start);
  const eventEnd = parseTs(data.event_end);
  const blockingStart = parseTs(data.blocking_start);
  const blockingEnd = parseTs(data.blocking_end);
  const detectionStart = parseTs(data.detection_start);
  const responseStart = parseTs(data.response_start);
  const impactStart = parseTs(data.impact_start);

  // Carry the cumulative curves flat to LEAST(now, response_stop_at): the last
  // bucket starts up to `interval` before the present, and the curves must
  // reach the NOW line rather than stop short of it.
  const bucketMs = data.interval_minutes * 60_000;
  const nowMs = Date.now();
  extendFlatToNow(detectionSeries, blockingEnd, nowMs);
  extendFlatToNow(responseSeries, blockingEnd, nowMs);
  extendFlatToNow(impactSeries, blockingEnd, nowMs);

  const detectionColor = colors.operational.detection;
  const responseColor = colors.operational.response;
  const impactColor = colors.operational.impact;
  const expectedDetectionsColor = colors.operational.detection;
  const harvestingStart = parseTs(refs.harvestingStart);
  const anomalyDetails = refs.anomalyDetails
    .map((anomaly) => ({ ...anomaly, ms: parseTs(anomaly.raised_at) }))
    .filter((anomaly): anomaly is typeof anomaly & { ms: number } => anomaly.ms !== null);

  // "Now" belongs to the chart only while the event is still going on: past
  // the end of everything plotted (plus one bucket of slack for a last
  // bucket that has not closed yet) it would just
  // stretch the axis, and before the first signal it points at nothing.
  const lastSignal = Math.max(
    ...[
      detectionSeries[detectionSeries.length - 1]?.[0],
      responseSeries[responseSeries.length - 1]?.[0],
      impactSeries[impactSeries.length - 1]?.[0],
      eventEnd,
      blockingEnd,
    ].filter((value): value is number => typeof value === "number" && Number.isFinite(value)),
  );
  const firstSignal = Math.min(
    ...[
      detectionSeries[0]?.[0],
      responseSeries[0]?.[0],
      impactSeries[0]?.[0],
      eventStart,
      blockingStart,
      harvestingStart,
    ].filter((value): value is number => typeof value === "number" && Number.isFinite(value)),
  );
  const nowInRange =
    Number.isFinite(lastSignal) &&
    Number.isFinite(firstSignal) &&
    nowMs >= firstSignal &&
    nowMs <= lastSignal + Math.max(bucketMs, 5 * 60_000);

  const signalTimes = [
    detectionSeries[0]?.[0],
    detectionSeries[detectionSeries.length - 1]?.[0],
    responseSeries[0]?.[0],
    responseSeries[responseSeries.length - 1]?.[0],
    impactSeries[0]?.[0],
    impactSeries[impactSeries.length - 1]?.[0],
    eventStart,
    eventEnd,
    blockingStart,
    blockingEnd,
    detectionStart,
    responseStart,
    impactStart,
    harvestingStart,
    ...anomalyDetails.map((anomaly) => anomaly.ms),
    nowInRange ? nowMs : undefined,
  ].filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  let min: number;
  let max: number;
  if (signalTimes.length > 0) {
    min = Math.min(...signalTimes);
    max = Math.max(...signalTimes);
  } else {
    min = parseTs(data.range_from) ?? Date.now() - 3_600_000;
    max = parseTs(data.range_to) ?? Date.now();
  }
  const span = Math.max(max - min, 60_000);
  const pad = span * 0.01;
  min -= pad;
  max += pad;

  const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };

  // ── Event window band ────────────────────────────────────────────────────
  const areaData: NonNullable<MarkAreaComponentOption["data"]> = [];
  if (eventStart !== null && eventEnd !== null) {
    areaData.push([
      {
        xAxis: eventStart,
        // The same slate as the axis labels, strong enough to tell the event
        // from the time around it, quiet enough to stay behind every curve.
        itemStyle: {
          color: withAlpha(colors.label, EVENT_WINDOW_FILL_ALPHA),
          borderColor: withAlpha(colors.label, EVENT_WINDOW_EDGE_ALPHA),
          borderWidth: 1,
        },
        label: {
          show: presentation?.showEventWindowLabel ?? true,
          // Anchored at the start of the band, not centred: a centred caption
          // is exactly where the Now line falls mid-event.
          position: "insideTopLeft",
          formatter: L.eventWindow,
          color: withAlpha(colors.label, 0.85),
          fontSize: colors.typography?.labelSize ?? 12,
          fontWeight: 600,
          ...fontBase,
        },
      },
      { xAxis: eventEnd },
    ]);
  }

  // ── Blocking period — two thin dashed vertical lines ────────────────────
  const blockingLines: NonNullable<MarkLineComponentOption["data"]> = [];
  if (blockingStart !== null) {
    blockingLines.push({
      name: "Blocking start",
      xAxis: blockingStart,
      lineStyle: { color: withAlpha(responseColor, 0.5), width: 1, type: "dashed" },
      label: {
        show: presentation?.showBlockingLabel ?? true,
        position: "insideEndBottom",
        formatter: L.blocking,
        color: withAlpha(responseColor, 0.65),
        fontSize: colors.typography?.labelSize ?? 12,
        fontWeight: 500,
        ...fontBase,
      },
    });
  }
  if (blockingEnd !== null) {
    blockingLines.push({
      name: "Blocking end",
      xAxis: blockingEnd,
      lineStyle: { color: withAlpha(responseColor, 0.4), width: 1, type: "dashed" },
      label: { show: false },
    });
  }

  // ── Series-start diamonds ────────────────────────────────────────────────
  type MarkPointDataItem = NonNullable<NonNullable<LineSeriesOption["markPoint"]>["data"]>[number];

  const detectionMarkPoints: MarkPointDataItem[] = [];
  if (detectionStart !== null && detectionSeries.length > 0) {
    const xMs = detectionSeries[0][0];
    detectionMarkPoints.push({
      name: "Detection start",
      coord: [xMs, interpolateY(detectionSeries, xMs)],
      symbol: "diamond",
      symbolSize: 9,
      itemStyle: { color: detectionColor, borderColor: detectionColor },
      label: {
        show: presentation?.showHarvestingLabel ?? true,
        position: "top",
        formatter: "D",
        color: detectionColor,
        fontSize: colors.typography?.labelSize ?? 12,
        fontWeight: 600,
        ...fontBase,
      },
    });
  }

  const responseMarkPoints: MarkPointDataItem[] = [];
  if (responseStart !== null && responseSeries.length > 0) {
    const xMs = responseSeries[0][0];
    responseMarkPoints.push({
      name: "Response start",
      coord: [xMs, interpolateY(responseSeries, xMs)],
      symbol: "diamond",
      symbolSize: 9,
      itemStyle: { color: responseColor, borderColor: responseColor },
      label: {
        show: presentation?.showBlockingLabel ?? true,
        position: "top",
        formatter: "R",
        color: responseColor,
        fontSize: colors.typography?.labelSize ?? 12,
        fontWeight: 600,
        ...fontBase,
      },
    });
  }

  const impactMarkPoints: MarkPointDataItem[] = [];
  if (impactStart !== null && impactSeries.length > 0) {
    const xMs = impactSeries[0][0];
    impactMarkPoints.push({
      name: "Impact start",
      coord: [xMs, interpolateY(impactSeries, xMs)],
      symbol: "diamond",
      symbolSize: 9,
      itemStyle: { color: impactColor, borderColor: impactColor },
      label: {
        show: presentation?.showBlockingLabel ?? true,
        position: "top",
        formatter: "I",
        color: impactColor,
        fontSize: colors.typography?.labelSize ?? 12,
        fontWeight: 600,
        ...fontBase,
      },
    });
  }

  // ── Harvesting start — solid blue vertical line, tied to Detections legend ─
  const detectionMarkLines: NonNullable<MarkLineComponentOption["data"]> = [];
  if (harvestingStart !== null) {
    detectionMarkLines.push({
      name: "Harvesting start",
      xAxis: harvestingStart,
      lineStyle: { color: withAlpha(detectionColor, 0.7), width: 1, type: "dashed" },
      label: {
        show: presentation?.showHarvestingLabel ?? true,
        position: "insideMiddleBottom",
        formatter: L.harvesting,
        color: withAlpha(detectionColor, 0.8),
        fontSize: colors.typography?.labelSize ?? 12,
        fontWeight: 500,
        ...fontBase,
      },
    });
  }

  // ── Expected detections — darker detection-blue horizontal reference line ─
  const expectedMarkLine =
    refs.expectedDetections !== null
      ? {
          silent: true,
          symbol: "none" as const,
          data: [
            {
              yAxis: refs.expectedDetections,
              lineStyle: {
                color: expectedDetectionsColor,
                width: 1,
                type: "dashed" as const,
              },
              label: {
                show: presentation?.showExpectedDetectionsLabel ?? true,
                position: "insideStartTop" as const,
                offset: [5, 0],
                formatter: `Expected ${Math.round(refs.expectedDetections)}`,
                color: expectedDetectionsColor,
                fontSize: colors.typography?.labelSize ?? 12,
                ...fontBase,
              },
            },
          ],
        }
      : undefined;

  // ── Anomalies — vertical lines labeled by type ─
  // One colour for every anomaly. An acknowledged one is told apart by a
  // check mark and a lighter stroke, so the colour code keeps a single meaning.
  const anomalyMarkLines = anomalyDetails.map((anomaly) => {
    const acknowledged = isAcknowledged(anomaly.anomaly_status);
    return {
      name: `Anomaly: ${anomaly.anomaly_type}`,
      xAxis: anomaly.ms,
      anomaly_id: anomaly.anomaly_id,
      lineStyle: {
        color: colors.anomaly,
        width: 1.5,
        type: "solid" as const,
        opacity: acknowledged ? 0.7 : 1,
      },
      label: {
        show: presentation?.showAnomalyTypeLabel ?? true,
        position: "insideStartBottom",
        formatter: acknowledged
          ? `${ACKNOWLEDGED_MARK} ${anomaly.anomaly_type}`
          : anomaly.anomaly_type,
        color: colors.anomaly,
        fontSize: colors.typography?.labelSize ?? 12,
        fontWeight: acknowledged ? 400 : 600,
        ...fontBase,
      },
    };
  }) as unknown as NonNullable<MarkLineComponentOption["data"]>;

  // ── Now — a neutral vertical line, re-placed every time the chart is built ─
  const nowMarkLine =
    nowInRange && nowMs >= min && nowMs <= max
      ? {
          silent: true,
          symbol: "none" as const,
          data: [
            {
              name: L.now,
              xAxis: nowMs,
              lineStyle: { color: colors.now, width: 1.5, type: "solid" as const },
              label: {
                show: presentation?.showNowLabel ?? true,
                // A small flag above the plot, in the strip between the legend
                // and the grid: it can never sit on the Event window caption
                // or on a curve, wherever the present falls.
                position: "end" as const,
                distance: 3,
                formatter: L.now.toUpperCase(),
                color: colors.now,
                // A notch under the axis labels, scaled with them (wall screens included).
                fontSize: Math.round(((colors.typography?.labelSize ?? 12) * 11) / 12),
                fontWeight: 700,
                ...fontBase,
              },
            },
          ],
        }
      : undefined;

  // ── Aligned value axes: 100 % on the rate axis = Expected detections ─
  const expectedAxis =
    refs.expectedDetections !== null && refs.expectedDetections > 0
      ? refs.expectedDetections
      : null;
  const countPeak = Math.max(0, ...detectionSeries.map((point) => point[1]));
  const ratePeak = Math.max(
    0,
    ...responseSeries.map((point) => point[1]),
    ...impactSeries.map((point) => point[1]),
  );
  const aligned = expectedAxis !== null ? alignedAxes(expectedAxis, countPeak, ratePeak) : null;
  const isExpectedTick = (value: number) =>
    expectedAxis !== null && Math.abs(value - expectedAxis) < 1e-6 * Math.max(1, expectedAxis);

  return {
    useUTC: timezone === "utc",
    animationDuration: 500,
    animationEasing: "cubicOut",
    backgroundColor: "transparent",
    grid: {
      top: presentation?.grid?.top ?? 44,
      right: presentation?.grid?.right ?? 52,
      bottom: presentation?.grid?.bottom ?? 8,
      left: presentation?.grid?.left ?? 52,
      containLabel: true,
    },
    legend: {
      show: options?.showLegend ?? true,
      top: 4,
      left: "center",
      itemWidth: 16,
      itemHeight: 2,
      itemGap: 16,
      type: "scroll",
      data: [L.detections, L.response, L.impact, L.expectedDetections, L.anomalies, L.now].map(
        (name) => ({
          name,
          icon: name === L.expectedDetections ? "path://M0 0H6V2H0Z M10 0H16V2H10Z" : "rect",
        }),
      ),
      textStyle: { color: colors.label, fontSize: colors.typography?.labelSize ?? 12, ...fontBase },
      ...(legendSelected
        ? { selected: legendSelected }
        : applyLegendDefaults
          ? { selected: { [L.expectedDetections]: true, [L.anomalies]: true } }
          : {}),
    },
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      transitionDuration: 0,
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: {
        color: colors.tooltipText,
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
      },
      axisPointer: {
        type: "line",
        // snap:false keeps axisValue on the raw cursor position; the default
        // (snap to nearest data point) makes anomaly-line hovers miss whenever
        // the anomaly sits far from a curve point.
        snap: false,
        lineStyle: { color: withAlpha(colors.label, 0.15), width: 1 },
      },
      formatter: (params: unknown) => {
        type TooltipItem = {
          componentType?: string;
          data?: SeriesPoint | { anomaly_id?: number };
          seriesName?: string;
          marker?: string;
          axisValue?: number;
        };
        const items = (Array.isArray(params) ? params : [params]) as TooltipItem[];

        const directAnomalyData = items.find(
          (item) => item.componentType === "markLine" && item.seriesName === L.anomalies,
        )?.data;
        if (anomaliesVisible && directAnomalyData && !Array.isArray(directAnomalyData)) {
          const hovered = anomalyDetails.find(
            (anomaly) => anomaly.anomaly_id === directAnomalyData.anomaly_id,
          );
          if (hovered) {
            const hoveredMinute = Math.floor(hovered.ms / 60_000);
            return renderAnomalyTooltip(
              anomalyDetails.filter((anomaly) => Math.floor(anomaly.ms / 60_000) === hoveredMinute),
              colors,
              timezone,
            );
          }
        }

        const anchor = items?.find((item) => Array.isArray(item.data))?.data;

        // Anomaly mode: while the Anomalies legend line is on, the tooltip is
        // dedicated to anomalies and curve tooltips are suppressed. We surface
        // the anomaly nearest the cursor, but hide entirely when the pointer is
        // clearly away from every bar; anomalies in the same minute are stacked.
        if (anomaliesVisible) {
          if (anomalyDetails.length === 0) return "";
          const pointerMs =
            typeof items?.[0]?.axisValue === "number"
              ? items[0].axisValue
              : Array.isArray(anchor)
                ? anchor[0]
                : null;
          if (pointerMs === null || !Number.isFinite(pointerMs)) return "";
          let nearest = anomalyDetails[0];
          let nearestDist = Infinity;
          for (const anomaly of anomalyDetails) {
            const dist = Math.abs(anomaly.ms - pointerMs);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearest = anomaly;
            }
          }
          const tolerance = Math.max((max - min) * 0.02, 3 * 60_000);
          if (nearestDist > tolerance) return "";
          const nearestMinute = Math.floor(nearest.ms / 60_000);
          const matched = anomalyDetails.filter(
            (anomaly) => Math.floor(anomaly.ms / 60_000) === nearestMinute,
          );
          return renderAnomalyTooltip(matched, colors, timezone);
        }

        if (!Array.isArray(anchor)) return "";

        const lines = [
          `<div style="margin-bottom:5px;font-size:0.75rem;opacity:0.75">${formatTooltipClock(anchor[0], timezone)}</div>`,
        ];
        for (const item of items) {
          if (!Array.isArray(item.data) || !item.seriesName) continue;
          const value = item.data[1];
          const isDetections = item.seriesName === L.detections;
          const formatted = isDetections ? `${Math.round(value)}` : `${Math.round(value * 100)}%`;
          lines.push(
            `<div style="display:flex;align-items:center;gap:6px;margin-top:2px">${item.marker ?? ""}<span style="opacity:0.75">${escapeHtml(isDetections ? L.cumulativeDetections : item.seriesName)}</span><span style="margin-left:auto;font-weight:600">${formatted}</span></div>`,
          );
        }
        return lines.join("");
      },
    },

    // ── DataZoom ─────────────────────────────────────────────────────────────
    // filterMode: 'none' keeps every data point in the series model regardless
    // of the viewport. Combined with clip: true on each series, ECharts computes
    // Bézier tangents over the full dataset (so smooth curves never kink at the
    // viewport edge) and then clips the canvas rendering at the grid boundary.
    // 'weakFilter' is insufficient: it still drops points that are "far enough"
    // outside the window, which causes the left portion of cumulative curves to
    // disappear when zooming into the right half of the chart.
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        minSpan: 3,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
      },
    ],

    xAxis: {
      type: "time",
      min,
      max,
      interval: presentation?.xAxisInterval,
      axisLabel: {
        color: colors.label,
        hideOverlap: true,
        margin: 10,
        fontSize: xAxisLabelFontSize,
        ...fontBase,
        formatter: (value: number) => formatAxisTime(value, timezone),
      },
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: "value",
        name: L.countAxis,
        nameLocation: "end",
        nameGap: 10,
        nameTextStyle: {
          color: withAlpha(detectionColor, 0.8),
          fontSize: colors.typography?.labelSize ?? 12,
          align: "right",
          padding: [0, 0, 0, 0],
          ...fontBase,
        },
        min: 0,
        // With an expected level, both axes are one scale (see alignedAxes);
        // otherwise each fits its own data.
        ...(aligned
          ? { max: aligned.countMax, interval: aligned.countInterval }
          : {
              max: (value: { max: number }) => Math.max(1, Math.ceil(value.max * 1.15)),
            }),
        axisLabel: {
          color: withAlpha(detectionColor, 0.7),
          fontSize: yAxisLabelFontSize,
          ...fontBase,
          // Aligned ticks are multiples of a share of the expected count, so
          // round them for display; bold + blue the one that IS the expected value.
          formatter: aligned
            ? (value: number) => {
                const text =
                  aligned.countInterval >= 1
                    ? `${Math.round(value)}`
                    : `${Math.round(value * 10) / 10}`;
                return showExpectedDetections && isExpectedTick(value)
                  ? `{expected|${text}}`
                  : text;
              }
            : undefined,
          rich: {
            expected: {
              color: expectedDetectionsColor,
              fontWeight: 700,
              fontSize: yAxisLabelFontSize,
              ...fontBase,
            },
          },
        },
        splitLine: { lineStyle: { color: colors.grid, type: "solid" } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      {
        type: "value",
        name: L.rateAxis,
        nameLocation: "end",
        nameGap: 10,
        nameTextStyle: {
          color: withAlpha(colors.label, 0.5),
          fontSize: colors.typography?.labelSize ?? 12,
          align: "left",
          padding: [0, 0, 0, 8],
          ...fontBase,
        },
        min: 0,
        // Rates may exceed 100% in the supplied/derived data. Preserve them;
        // never hide the excess by clamping the domain or the measured values.
        ...(aligned
          ? { max: aligned.rateMax, interval: aligned.rateInterval }
          : {
              max: (value: { max: number }) =>
                Math.max(1.05, Math.ceil(value.max * 1.05 * 20) / 20),
            }),
        axisLabel: {
          color: colors.label,
          fontSize: yAxisLabelFontSize,
          ...fontBase,
          // 100 % is the target, and it faces the Expected detections tick.
          formatter: (value: number) =>
            aligned && showExpectedDetections && Math.abs(value - 1) < 1e-9
              ? `{full|100%}`
              : `${Math.round(value * 100)}%`,
          rich: {
            full: {
              color: colors.label,
              fontSize: yAxisLabelFontSize,
              ...fontBase,
            },
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
    ],
    series: [
      // ── Detections ───────────────────────────────────────────────────────
      // clip: true ensures canvas rendering is clipped at the grid boundary
      // when filterMode is 'none' (all points remain in the model).
      {
        type: "line",
        name: L.detections,
        yAxisIndex: 0,
        z: 1,
        clip: true,
        data: detectionSeries,
        smooth: detectionRepresentation === "smooth-line" ? 0.3 : false,
        step: detectionRepresentation === "smooth-line" ? undefined : "end",
        showSymbol: false,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: {
          width: detectionLineWidth,
          color: detectionColor,
        },
        areaStyle: steppedArea
          ? {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: withAlpha(detectionColor, areaFillAlpha) },
                  { offset: 0.65, color: withAlpha(detectionColor, areaFillAlpha * 0.5) },
                  { offset: 1, color: withAlpha(detectionColor, areaFillAlpha * 0.12) },
                ],
                global: false,
              },
              opacity: 1,
            }
          : undefined,
        itemStyle: { color: detectionColor },
        emphasis: { focus: "series", lineStyle: { width: detectionLineWidth } },
        markArea: areaData.length > 0 ? { z: 0, silent: true, data: areaData } : undefined,
        markLine:
          detectionMarkLines.length > 0
            ? { z: 3, silent: true, symbol: "none", data: detectionMarkLines }
            : undefined,
        markPoint:
          detectionMarkPoints.length > 0 ? { silent: true, data: detectionMarkPoints } : undefined,
      },

      // ── Responses ────────────────────────────────────────────────────────
      {
        type: "line",
        name: L.response,
        yAxisIndex: 1,
        z: 2,
        clip: true,
        data: responseSeries,
        smooth: false,
        showSymbol: false,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 2, color: responseColor },
        itemStyle: { color: responseColor },
        emphasis: { focus: "series", lineStyle: { width: 2.5 } },
        markLine:
          blockingLines.length > 0
            ? { silent: true, symbol: "none", data: blockingLines }
            : undefined,
        markPoint:
          responseMarkPoints.length > 0 ? { silent: true, data: responseMarkPoints } : undefined,
      },

      // ── Impacts ──────────────────────────────────────────────────────────
      {
        type: "line",
        name: L.impact,
        yAxisIndex: 1,
        z: 2,
        clip: true,
        data: impactSeries,
        smooth: false,
        showSymbol: false,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 2, color: impactColor },
        itemStyle: { color: impactColor },
        emphasis: { focus: "series", lineStyle: { width: 2.5 } },
        markPoint:
          impactMarkPoints.length > 0 ? { silent: true, data: impactMarkPoints } : undefined,
      },

      // ── Expected detections — empty series carrying a grey horizontal line ──
      {
        type: "line",
        name: L.expectedDetections,
        yAxisIndex: 0,
        z: 3,
        data: [],
        silent: true,
        showSymbol: false,
        lineStyle: { color: expectedDetectionsColor, type: "dashed" },
        itemStyle: { color: expectedDetectionsColor },
        markLine: expectedMarkLine,
      },

      // ── Anomalies — empty series carrying the vertical lines ───────────────
      {
        type: "line",
        name: L.anomalies,
        yAxisIndex: 0,
        z: 4,
        data: [],
        showSymbol: false,
        lineStyle: { color: colors.anomaly },
        itemStyle: { color: colors.anomaly },
        markLine:
          anomalyMarkLines.length > 0
            ? {
                silent: false,
                symbol: "none",
                tooltip: { trigger: "item" },
                emphasis: { lineStyle: { width: 4 } },
                data: anomalyMarkLines,
              }
            : undefined,
      },

      // ── Now — empty series carrying the neutral vertical line ───────────────
      // Its own legend entry, so it can be hidden without touching a metric.
      {
        type: "line",
        name: L.now,
        yAxisIndex: 0,
        z: 5,
        data: [],
        silent: true,
        showSymbol: false,
        lineStyle: { color: colors.now, width: 2 },
        itemStyle: { color: colors.now },
        markLine: nowMarkLine,
      },
    ],
  };
}

export function latestEvolutionPoint(points: EventEvolutionPoint[]): EventEvolutionPoint | null {
  return points
    .filter((point) => parseTs(point.timestamp) !== null && Number.isFinite(point.cumulative))
    .reduce<EventEvolutionPoint | null>(
      (latest, point) =>
        !latest || parseTs(point.timestamp)! > parseTs(latest.timestamp)! ? point : latest,
      null,
    );
}

function EvolutionSummary({
  data,
  labels = DEFAULT_LABELS,
}: {
  data: EventEvolutionData;
  labels?: EventEvolutionLabels;
}) {
  const timezone = useDisplayTimezone();
  const metrics = [
    {
      label: labels.summaryDetections,
      points: data.detections,
      color: "text-data-detection",
      rate: false,
    },
    {
      label: labels.summaryResponse,
      points: data.responses,
      color: "text-data-response",
      rate: true,
    },
    { label: labels.summaryImpact, points: data.impacts, color: "text-data-impact", rate: true },
  ];
  return (
    <div className="mx-4 mb-4 grid shrink-0 grid-cols-1 gap-4 border-b border-border pb-4 @sm:grid-cols-3">
      {metrics.map((metric) => {
        const point = latestEvolutionPoint(metric.points);
        const value = point
          ? new Intl.NumberFormat(
              undefined,
              metric.rate
                ? { style: "percent", maximumFractionDigits: 1 }
                : { notation: "compact", maximumFractionDigits: 1 },
            ).format(point.cumulative)
          : "—";
        return (
          <div key={metric.label} className="min-w-0">
            <p className="text-sm text-secondary">{metric.label}</p>
            <p
              className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${metric.color}`}
            >
              {value}
            </p>
            <p className="mt-1 text-xs text-muted">
              {point
                ? formatTooltipClock(parseTs(point.timestamp)!, timezone)
                : labels.dataUnavailable}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function EventEvolutionComponent({ data, config, dashboard }: WidgetProps<EventEvolutionConfig>) {
  const timezone = useDisplayTimezone();
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen: inFocusOverlay } = useContext(WidgetFullscreenContext);
  const labelOverrides = (config as { labels?: unknown }).labels;
  const labels = useMemo(() => resolveLabels(LABELS, { labels: labelOverrides }), [labelOverrides]);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const error = firstError(data, ["evolution"]);
  const resolved = unwrap<EventEvolutionData>(data.evolution);
  const isLoading = data.evolution?.isLoading ?? false;

  const hasScope =
    typeof config.protectedEventId === "number" ||
    dashboard.filters.dataFilters.some((f) => f.field === "protected_event_id");

  const title = config.title ?? defaultConfig.title ?? "Event Evolution";
  const compact = config.compact ?? false;
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const hasRenderedRef = useRef(false);
  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;
  const dataSet = resolved ?? null;

  const [isZoomed, setIsZoomed] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [isExpectedDetectionsVisible, setIsExpectedDetectionsVisible] = useState(true);
  const [isAnomaliesVisible, setIsAnomaliesVisible] = useState(true);

  // Reset pill selection whenever the scoped event changes.
  const protectedEventId = dataSet?.protected_event_id;
  useEffect(() => {
    setSelectedValue(null);
  }, [protectedEventId]);

  const filterDimension = config.filterDimension;
  const breakdown = dataSet?.breakdown ?? [];
  const showPills = !compact && Boolean(filterDimension) && breakdown.length > 0;
  const fixedBreakdown = config.breakdownValue;
  const activeValues = useMemo(
    () => new Set(fixedBreakdown ? [fixedBreakdown] : selectedValue ? [selectedValue] : []),
    [fixedBreakdown, selectedValue],
  );
  const monitoredEntry = breakdown.find(
    (entry) => entry.value === (fixedBreakdown ?? selectedValue),
  );
  const monitoringLabel = (monitoredEntry?.monitoring_engines ?? [])
    .map((engine) => engine.engine_label)
    .join(", ");

  const derived = useMemo(
    () => (dataSet ? deriveFilteredEvolution(dataSet, activeValues) : null),
    [activeValues, dataSet],
  );
  // A dataset can technically resolve (breakdown entries exist per configured
  // engine) while carrying nothing to actually plot — no series points, no
  // reference markers, no anomalies. Rendering the full chart frame + summary
  // row in that case just reserves a tall, blank half of the dashboard (e.g.
  // a "CDN / SA" group with no matching detections yet). Treat that the same
  // as "no dataset" so it collapses to the same compact empty state instead.
  const hasEvolutionContent = Boolean(
    derived &&
    (derived.data.detections.length > 0 ||
      derived.data.responses.length > 0 ||
      derived.data.impacts.length > 0 ||
      derived.refs.anomalyDetails.length > 0 ||
      derived.refs.expectedDetections !== null ||
      derived.refs.harvestingStart !== null),
  );
  const isEmpty = !isLoading && !error && !hasEvolutionContent;
  const seriesVisibility = config.seriesVisibility;
  const expectedDetectionsVisible =
    seriesVisibility?.["Expected detections"] ?? isExpectedDetectionsVisible;
  const anomaliesVisible = seriesVisibility?.Anomalies ?? isAnomaliesVisible;
  const detectionRepresentation =
    config.detectionRepresentation ?? defaultConfig.detectionRepresentation;
  const compactMissingData =
    compact &&
    Boolean(fixedBreakdown) &&
    !breakdown.some((entry) => entry.value === fixedBreakdown);
  const titleClassName = compact
    ? isEmpty || compactMissingData
      ? "!text-2xs font-semibold !tracking-normal !text-muted"
      : inFocusOverlay
        ? "!text-base font-semibold !tracking-normal !text-brand-navy dark:!text-primary"
        : "!text-xs font-semibold !tracking-normal !text-brand-navy dark:!text-primary"
    : undefined;
  const compactPresentation: EventEvolutionPresentationOptions | undefined = compact
    ? inFocusOverlay
      ? {
          xAxisLabelFontSize: 12,
          yAxisLabelFontSize: 12,
          xAxisInterval: 60 * 60_000,
          areaFillAlpha: 0.1,
          detectionLineWidth: 1.5,
          grid: { top: 46, right: 24, bottom: 28, left: 24 },
          showHarvestingLabel: false,
          showEventWindowLabel: true,
          showExpectedDetectionsLabel: true,
          showAnomalyTypeLabel: false,
          showBlockingLabel: false,
          showNowLabel: true,
        }
      : {
          xAxisLabelFontSize: 12,
          yAxisLabelFontSize: 12,
          xAxisInterval: 60 * 60_000,
          detectionLineWidth: 1.5,
          grid: { top: 8, right: 4, bottom: 22, left: 4 },
          showHarvestingLabel: false,
          showEventWindowLabel: false,
          showExpectedDetectionsLabel: false,
          showAnomalyTypeLabel: false,
          showBlockingLabel: false,
          showNowLabel: false,
        }
    : undefined;
  const derivedRef = useRef(derived);
  useEffect(() => {
    derivedRef.current = derived;
  }, [derived]);
  const expectedDetectionsVisibleRef = useRef(isExpectedDetectionsVisible);
  useEffect(() => {
    expectedDetectionsVisibleRef.current = isExpectedDetectionsVisible;
  }, [isExpectedDetectionsVisible]);
  const anomaliesVisibleRef = useRef(isAnomaliesVisible);
  useEffect(() => {
    anomaliesVisibleRef.current = isAnomaliesVisible;
  }, [isAnomaliesVisible]);

  useEffect(() => {
    const host = chartRef.current;
    if (!host || error || isEmpty || isLoading || !derived) return;

    let freshInit = false;
    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      chartInstanceRef.current = echarts.init(host, null, { renderer: "canvas" });
      freshInit = true;
    }

    const applyLegendDefaults = !hasRenderedRef.current;
    chartInstanceRef.current.setOption(
      buildOption(
        derived.data,
        resolveChartColors(host),
        derived.refs,
        applyLegendDefaults,
        expectedDetectionsVisible,
        anomaliesVisible,
        {
          timezone,
          showLegend: !compact || inFocusOverlay,
          legendSelected: seriesVisibility,
          presentation: compactPresentation,
          detectionRepresentation,
          labels,
        },
      ),
    );
    hasRenderedRef.current = true;
    markContentReady();

    if (freshInit) {
      setIsZoomed(false);
      chartInstanceRef.current.on("datazoom", (params: unknown) => {
        type DataZoomParams = {
          start?: number;
          end?: number;
          batch?: Array<{ start: number; end: number }>;
        };
        const p = params as DataZoomParams;
        const start = p.batch?.[0]?.start ?? p.start ?? 0;
        const end = p.batch?.[0]?.end ?? p.end ?? 100;
        setIsZoomed(start > 0.5 || end < 99.5);
      });
      chartInstanceRef.current.on("legendselectchanged", (params: unknown) => {
        const selected = (params as { selected?: Record<string, boolean> }).selected;
        setIsExpectedDetectionsVisible(selected?.[labelsRef.current.expectedDetections] ?? false);
        setIsAnomaliesVisible(selected?.[labelsRef.current.anomalies] ?? false);
      });
      chartInstanceRef.current.on("click", (params: unknown) => {
        const p = params as {
          componentType?: string;
          seriesName?: string;
          data?: { anomaly_id?: number };
        };
        if (p.componentType !== "markLine" || p.seriesName !== labelsRef.current.anomalies) return;
        const anomalyId = p.data?.anomaly_id;
        if (typeof anomalyId !== "number") return;
        const details = derivedRef.current?.refs.anomalyDetails ?? [];
        const clicked = details.find((a) => a.anomaly_id === anomalyId);
        const siblings = details.map((a) => ({
          anomaly_id: a.anomaly_id,
          anomaly_type: a.anomaly_type,
          anomaly_severity: a.anomaly_severity,
          anomaly_status: a.anomaly_status,
        }));
        dashboardRef.current.emitInteraction?.({
          type: "anomaly.select",
          payload: {
            anomaly_id: anomalyId,
            anomaly_type: clicked?.anomaly_type,
            anomaly_severity: clicked?.anomaly_severity,
            anomaly_status: clicked?.anomaly_status,
            visible_anomalies: siblings,
          } as unknown as JsonValue,
          source: { dataKey: "evolution" },
        });
      });
    }
  }, [
    anomaliesVisible,
    compact,
    detectionRepresentation,
    derived,
    error,
    expectedDetectionsVisible,
    isEmpty,
    isLoading,
    inFocusOverlay,
    labels,
    markContentReady,
    seriesVisibility,
    timezone,
  ]);

  const handleResetZoom = () => {
    chartInstanceRef.current?.dispatchAction({
      type: "dataZoom",
      dataZoomIndex: 0,
      start: 0,
      end: 100,
    });
    setIsZoomed(false);
  };

  useChartLifecycle(
    chartRef,
    chartInstanceRef,
    (host, chart) => {
      const current = derivedRef.current;
      if (!hasRenderedRef.current || !current) return;
      chart.setOption(
        buildOption(
          current.data,
          resolveChartColors(host),
          current.refs,
          false,
          seriesVisibility?.["Expected detections"] ?? expectedDetectionsVisibleRef.current,
          seriesVisibility?.Anomalies ?? anomaliesVisibleRef.current,
          {
            timezone,
            showLegend: !compact || inFocusOverlay,
            legendSelected: seriesVisibility,
            presentation: compactPresentation,
            detectionRepresentation,
            labels: labelsRef.current,
          },
        ),
      );
    },
    `${hasScope}:${Boolean(error)}`,
  );

  useEffect(() => {
    if (hasScope) return;
    const chart = chartInstanceRef.current;
    if (!chart || chart.isDisposed()) return;
    chart.dispose();
    chartInstanceRef.current = null;
    hasRenderedRef.current = false;
  }, [hasScope]);

  if (!hasScope) {
    return (
      <Widget title={title} titleClassName={titleClassName}>
        <EmptyDataState />
      </Widget>
    );
  }

  if (error) {
    console.error("[EventEvolutionWidget] Failed to load data:", error);
    return (
      <Widget title={title} titleClassName={titleClassName}>
        <WidgetIssueState error={error} className="py-5" />
      </Widget>
    );
  }

  if (isEmpty || compactMissingData) {
    return (
      <Widget title={title} titleClassName={titleClassName}>
        {isLoading ? (
          <WidgetSkeleton shape="chart" className={compact ? "min-h-48" : "min-h-56"} />
        ) : (
          <div
            className={`flex h-full items-center justify-center px-3 text-center ${compact ? "min-h-48 rounded-md bg-surface-elevated text-2xs text-muted" : "py-5 text-sm text-muted"}`}
          >
            <EmptyDataState />
          </div>
        )}
      </Widget>
    );
  }

  const zoomAction = isZoomed ? (
    <button
      type="button"
      title={labels.resetZoom}
      onClick={handleResetZoom}
      className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-brand-cyan transition-colors hover:bg-surface-elevated hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
      {labels.resetZoom}
    </button>
  ) : null;
  const monitoringDetails = monitoringLabel ? (
    <span className="flex flex-col">
      <span className="text-muted">{labels.monitoredBy}</span>
      <span className="font-medium text-primary">{monitoringLabel}</span>
    </span>
  ) : null;
  const monitoringTooltip = monitoringLabel ? (
    <InfoTip label={`Monitoring details for ${fixedBreakdown ?? selectedValue}`} width={192}>
      {monitoringDetails}
    </InfoTip>
  ) : null;

  const breakdownControls = showPills ? (
    <div
      className="flex min-w-0 flex-wrap items-center justify-end gap-1.5"
      role="group"
      aria-label={`${title} breakdown`}
    >
      <ToggleChip pressed={selectedValue === null} onPressedChange={() => setSelectedValue(null)}>
        ALL
      </ToggleChip>
      {breakdown.map((entry) => {
        const active = selectedValue === entry.value;
        return (
          <ToggleChip
            key={entry.value}
            pressed={active}
            onPressedChange={() => setSelectedValue(entry.value)}
          >
            <span>{formatDetectionDomain(entry.value)}</span>
            {entry.anomaly_count > 0 ? (
              <span className="inline-flex items-center gap-0.5 text-status-error-text">
                <AlertTriangleIcon />
                {entry.anomaly_count}
              </span>
            ) : null}
          </ToggleChip>
        );
      })}
      {monitoringTooltip}
    </div>
  ) : null;

  return (
    <Widget
      title={title}
      titleClassName={titleClassName}
      intrinsicHeight={!compact && !inFocusOverlay}
      actions={
        compact ? (
          monitoringTooltip
        ) : (
          <>
            {breakdownControls}
            {zoomAction}
          </>
        )
      }
    >
      <div
        className={`@container flex w-full flex-col ${compact ? "h-full min-h-48" : inFocusOverlay ? "h-full min-h-0 overflow-y-auto" : "flex-1"}`}
      >
        {!compact && derived && <EvolutionSummary data={derived.data} labels={labels} />}
        <div
          className={`relative w-full flex-1 ${compact ? "min-h-0" : "min-h-(--widget-chart-min-height)"}`}
          onDoubleClick={handleResetZoom}
        >
          <div
            ref={chartRef}
            role="img"
            aria-label="Cumulative detections, response and impact on the percentage axis"
            className="absolute inset-0 h-full w-full"
          />
        </div>
      </div>
    </Widget>
  );
}

export const Component = EventEvolutionComponent;

export const module: WidgetModule = {
  manifest,
  Component: Component as unknown as WidgetModule["Component"],
  defaultConfig,
};
